<template>
  <v-app>
    <!-- ================= SEARCH PANEL ================= -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="From Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        label="To Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-btn type="submit" color="primary" class="mt-1">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= TABLE PANEL ================= -->
    <v-container fluid style="margin-top:-20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                TT Allocation Records
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- Top Actions -->
                <v-row class="py-2 px-2">
                  <v-col md="4">
                    <v-text-field
                      v-model="search"
                      label="Search"
                      append-inner-icon="mdi-magnify"
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    />
                  </v-col>

                  <v-col md="5" class="text-center">
                    <v-btn-group density="compact" divided>
                      <v-btn color="success" @click="exportToCSV">CSV</v-btn>
                      <v-btn color="primary" @click="exportToExcel">EXCEL</v-btn>
                      <v-btn color="red" @click="exportToPDF">PDF</v-btn>
                    </v-btn-group>
                  </v-col>

                  <v-col md="3" class="text-right">
                    <v-btn
                      color="primary"
                      prepend-icon="mdi-plus"
                      rounded="xl"
                      variant="tonal"
                      @click="goToCreate"
                    >
                      Add TT Allocation
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- ================= DATA TABLE ================= -->
                <v-data-table
                  :headers="headers"
                  :items="ttAllocationList"
                  :loading="loading"
                  :search="search"
                  v-model:items-per-page="itemsPerPage"
                  density="compact"
                >
                  <!-- Index -->
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <!-- Status -->
                  <template #item.status="{ item }">
                    <v-chip
                      size="small"
                      :color="item.status === 1 ? 'success' : 'red'"
                    >
                      {{ item.status === 1 ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>

                  <!-- Actions -->
                  <template #item.actions="{ item }">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      color="primary"
                      @click="goToEdit(item.id)"
                    />
                    <v-btn
                      icon="mdi-file"
                      size="small"
                      variant="text"
                      color="info"
                      @click="viewDetails(item.id)"
                    />
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

const store = useStore();
const router = useRouter();

const panel = ref([0]);
const search = ref("");
const loading = ref(false);
const itemsPerPage = ref(10);

/* ================= FORM ================= */
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

/* ================= TABLE HEADERS ================= */
const headers = [
  { title: "#", key: "index", sortable: false,align: "center", width: "120px" },
  { title: "Code", key: "code" },
  { title: "Buyer", key: "buyer.name" },
  { title: "FTT Reference", key: "details.ftt_reference" },
  { title: "TT Utilization Amount", key: "details.tt_utilization_amount" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];


const ttAllocationList = computed(() =>
  store.getters.getTtAllocationList || []
);

/* ================= METHODS ================= */
const submit = handleSubmit(() => {
  store.dispatch("getTtAllocationList", {from_date:from_date, to_date:to_date});
});

const goToCreate = () => {
  router.push({ name: "tt-allocation-create" });
};

const goToEdit = (id) => {
  router.push({ name: "tt-allocation-create", params: { id } });
};

const viewDetails = (id) => {
  router.push({ name: "tt-allocation-view", params: { id } });
};

const exportToCSV = () => toast.info("CSV export coming soon");
const exportToExcel = () => toast.info("Excel export coming soon");
const exportToPDF = () => toast.info("PDF export coming soon");

/* ================= ON MOUNT ================= */
onMounted(() => {
  loading.value = true;
  store.dispatch("getTtAllocationList")
    .finally(() => loading.value = false);
});
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>

